import {activeDeployment, isAllowedProductHostUrl} from "./deployment-config.js";

// Deployment-config seams. Historically these were TwoShot literals; they now
// read the ACTIVE deployment lazily (inside each function, never at module
// scope) so the server/SPA can install its config after import. The TwoShot
// defaults in deployment-config.ts reproduce the old literals exactly, which
// login-return-to.test.ts pins value-by-value rather than asserting.
//
// Predicates and builders agree on the same condition: on a deployment with no
// native app (returnTo.nativeScheme === null) every native predicate answers
// false AND every native builder returns null. An earlier revision defaulted
// the builders to TwoShot's scheme, so a Forge build emitted
// app.twoshot.mobile: URLs that its own allowlist then refused.
const NATIVE_PROVIDER_LOGIN_PATH = "/login/provider/";
const NATIVE_PARENT_ORIGIN_PARAM = "native_parent_origin";

function loginCallbackPath(): string {
    return activeDeployment().returnTo.loginCallbackPath;
}

/** Native scheme, or null when this deployment has no native app (every
 *  native-handoff predicate then answers false). */
function nativeScheme(): string | null {
    return activeDeployment().returnTo.nativeScheme;
}

/** TwoShot's native callback URL, as an eagerly-usable string for consumers
 *  that cannot call a function (App.tsx's no-handoff-ticket branch navigates
 *  straight to it).
 *
 *  THE THREE SLASHES ARE LOAD-BEARING. This is `scheme:` + `//` + `/login/callback`,
 *  which parses as host="" pathname="/login/callback". Writing it with two
 *  slashes yields host="login" pathname="/callback" -- a DIFFERENT deep link
 *  that iOS would not route. Pinned in login-return-to.test.ts.
 *
 *  Deliberately a literal, not a config read: this is module scope, and a
 *  config read here would capture the default before the deployment installs
 *  its own (the failure this file's header warns about). A deployment with no
 *  native app never reaches it -- every native predicate answers false, and
 *  every native builder now returns null. */
export const NATIVE_APP_CALLBACK_URL = "app.twoshot.mobile:///login/callback";

/** The deployment's native callback base, or null when it has no native app.
 *  Null-returning rather than falling back to TwoShot's scheme: a Forge build
 *  emitting an app.twoshot.mobile: URL is wrong even where it is unreachable. */
function nativeCallbackBase(): string | null {
    const scheme = nativeScheme();
    return scheme === null ? null : `${scheme}//${loginCallbackPath()}`;
}

export function getDefaultExternalReturnTo(): string {
    return activeDeployment().returnTo.defaultExternal;
}

function isAllowedLocalFrontendPort(port: string): boolean {
    const parsedPort = Number.parseInt(port, 10);
    return (parsedPort >= 3000 && parsedPort <= 3999) || parsedPort === 14203;
}

export function isAllowedLocalFrontendOrigin(u: URL): boolean {
    return u.protocol === "http:" &&
        (u.hostname === "localhost" || u.hostname === "127.0.0.1" || u.hostname === "[::1]" || u.hostname === "::1") &&
        isAllowedLocalFrontendPort(u.port);
}

function isLocalFrontendLoginCallback(u: URL): boolean {
    return isAllowedLocalFrontendOrigin(u) &&
        u.pathname === loginCallbackPath();
}

/** https URL on one of the deployment's product hosts (apex or subdomain). */
function isProductReturnTo(u: URL): boolean {
    return isAllowedProductHostUrl(u, activeDeployment());
}

function getNativeCallbackPath(u: URL): string {
    if (u.hostname) return `/${u.hostname}${u.pathname}`;
    return u.pathname;
}

function isNativeAppLoginCallback(u: URL): boolean {
    const scheme = nativeScheme();
    return scheme !== null &&
        u.protocol === scheme &&
        getNativeCallbackPath(u) === loginCallbackPath();
}

function isFrontendLoginCallback(u: URL): boolean {
    return (
        u.protocol === "https:" &&
        activeDeployment().returnTo.apexHosts.includes(u.hostname) &&
        u.pathname === loginCallbackPath()
    ) || isLocalFrontendLoginCallback(u);
}

export function isNativeAppLoginReturnTo(raw: string): boolean {
    try {
        return isNativeAppLoginCallback(new URL(raw));
    } catch {
        return false;
    }
}

export function isNativeProviderLoginReturnTo(raw: string): boolean {
    try {
        const u = new URL(raw);
        return isNativeAppLoginCallback(u) || isFrontendLoginCallback(u);
    } catch {
        return false;
    }
}

/** Null when the deployment has no native app. */
export function buildNativeProviderLoginUrl(provider: string, returnTo: string): string | null {
    const scheme = nativeScheme();
    if (scheme === null) return null;
    const url = new URL(`${scheme}//${NATIVE_PROVIDER_LOGIN_PATH}${provider}`);
    url.searchParams.set("return_to", returnTo);
    return url.toString();
}

/** Null when the deployment has no native app. */
export function buildNativeAppLoginReturnTo(appReturnTo: string): string | null {
    const base = nativeCallbackBase();
    if (base === null) return null;
    const url = new URL(base);
    url.searchParams.set("app_return_to", appReturnTo);
    return url.toString();
}

export function buildNativeExternalLoginReturnTo(rawReturnTo: string): string | null {
    try {
        const u = new URL(rawReturnTo);
        if (!isFrontendLoginCallback(u) && !isNativeAppLoginCallback(u)) {
            return null;
        }
        return buildNativeAppLoginReturnTo(getNativeAppReturnPath(rawReturnTo));
    } catch {
        return null;
    }
}

export function getNativeParentOrigin(rawReturnTo: string): string {
    const cfg = activeDeployment().returnTo;
    try {
        const u = new URL(rawReturnTo);
        const parentOrigin = u.searchParams.get(NATIVE_PARENT_ORIGIN_PARAM);
        if (parentOrigin !== null && cfg.nativeParentOrigins.includes(parentOrigin)) {
            return parentOrigin;
        }
    } catch {
        /* fall through */
    }

    return cfg.defaultParentOrigin;
}

export function getNativeAppReturnPath(raw: string): string {
    try {
        const u = new URL(raw);
        return u.searchParams.get("app_return_to") || "/";
    } catch {
        return "/";
    }
}

/** Null when the deployment has no native app. */
export function buildNativeAppCallbackUrl(appReturnTo: string, handoffTicket: string): URL | null {
    const base = nativeCallbackBase();
    if (base === null) return null;
    const callbackUrl = new URL(base);
    callbackUrl.searchParams.set("app_return_to", appReturnTo);
    callbackUrl.searchParams.set("handoff_ticket", handoffTicket);
    return callbackUrl;
}

/** Null when the deployment has no native app (no scheme or no package id). */
export function buildNativeAppIntentUrl(callbackUrl: URL): string | null {
    const scheme = nativeScheme();
    const packageId = activeDeployment().returnTo.nativePackageId;
    if (scheme === null || packageId === null) return null;
    const callbackPath = getNativeCallbackPath(callbackUrl);
    return `intent://${callbackPath}${callbackUrl.search}#Intent;scheme=${scheme.replace(/:$/, "")};package=${packageId};end`;
}

/** A return target that CONSUMES the completion token pair: the product frontend's login
 *  callback on an exact apex host at the default port (or a local dev callback), or the native
 *  app callback. Narrower on purpose than isAllowedExternalLoginReturnTo, which admits any https
 *  page on any product host as somewhere to land: several of those pages redirect off-site
 *  (twoshot.app/discord -> discord.gg), and a token fragment rides that redirect (RFC 9110
 *  10.2.2). Unparseable, relative or credential-bearing URLs are not callbacks. */
export function isLoginCallbackReturnTo(raw: string): boolean {
    try {
        const u = new URL(raw);
        if (u.username || u.password) return false;
        if (isNativeAppLoginCallback(u)) return true;
        return isFrontendLoginCallback(u) && (u.protocol !== "https:" || u.port === "");
    } catch {
        return false;
    }
}

export function isAllowedExternalLoginReturnTo(u: URL): boolean {
    return isProductReturnTo(u) || isLocalFrontendLoginCallback(u) || isNativeAppLoginCallback(u);
}

export function resolveExternalLoginReturnTo(raw: string | null | undefined): string {
    if (!raw) return getDefaultExternalReturnTo();
    try {
        const u = new URL(raw);
        return isAllowedExternalLoginReturnTo(u) ? u.toString() : getDefaultExternalReturnTo();
    } catch {
        return getDefaultExternalReturnTo();
    }
}
